import React from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS, setAppLanguage, AppLanguage } from './index';

interface LanguageSwitcherProps {
  /** Color scheme. `dark` suits the sidebar; `light` suits a white MUI menu. */
  tone?: 'dark' | 'light';
  className?: string;
}

/**
 * Manual language switcher. Renders one button per supported language and
 * persists the choice (via setAppLanguage). Language names are endonyms
 * (English / 日本語) and are intentionally not translated.
 */
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ tone = 'dark', className }) => {
  // useTranslation subscribes the component to language changes so the active
  // selection re-renders immediately.
  const { i18n, t } = useTranslation('misc');
  const active = (i18n.language || 'en').split('-')[0] as AppLanguage;
  const idleColor = tone === 'dark' ? 'rgb(var(--pcg-fg) / 0.7)' : 'rgba(0,0,0,0.6)';
  const idleBg = tone === 'dark' ? 'rgb(var(--pcg-fg) / 0.08)' : 'rgba(0,0,0,0.06)';

  return (
    <div
      className={className}
      role="group"
      aria-label={t('languageSwitcher.ariaLabel')}
      style={{ display: 'inline-flex', gap: 4 }}
    >
      {SUPPORTED_LANGUAGES.map((lng) => {
        const selected = active === lng;
        return (
          <button
            key={lng}
            type="button"
            aria-pressed={selected}
            onClick={() => setAppLanguage(lng)}
            style={{
              cursor: 'pointer',
              border: 'none',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: '"Inter", sans-serif',
              background: selected
                ? 'linear-gradient(180deg, rgb(var(--pcg-gold)) 0%, rgb(var(--pcg-gold-muted)) 100%)'
                : idleBg,
              color: selected ? '#111' : idleColor,
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            {LANGUAGE_LABELS[lng]}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
